<template>
    <vx-table-row data-test="group-row" :class="rowClasses">
        <vx-table-row-title>
            <vx-badge v-if="setupRequired" color="rose" size="xs">Setup Required <font-awesome-icon widthAuto :icon="faAsterisk" class="text-rose-500" /></vx-badge>
            <template v-else>
                {{ group.name }}
            </template>
        </vx-table-row-title>

        <vx-table-row-item>
            <template v-slot:label> Status: </template>
            <group-status-badge v-if="group.status" :status="group.status" />
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> External ID: </template>
            <span class="break-all">{{ group.group_id }}</span>
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> Users: </template>
            <vx-icon-loading v-if="loadingUsersCount" spin></vx-icon-loading>
            <span v-else>{{ usersCount }}</span>
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> OU: </template>
            <vx-popover v-if="group.org_unit?.name" hover :arrow="true" placement="top">
                <font-awesome-icon widthAuto :icon="faBuildingFlag"></font-awesome-icon>
                <template v-slot:content>
                    <span class="text-sm font-normal">{{ group.org_unit.name }}</span>
                </template>
            </vx-popover>
        </vx-table-row-item>

        <vx-table-row-buttons v-if="props.isAdminOrOwner">
            <vx-dropdown v-if="!setupRequired" placement="right">
                <vx-button data-test="dropdown"
                    type="button"
                    :color="buttonColor"
                    outline
                    class="w-[45px]">
                    <font-awesome-icon widthAuto :icon="faEllipsis" />
                </vx-button>

                <template v-slot:items>
                    <vx-dropdown-item v-if="props.isKiosk" data-test="change-status"
                        @click="statusModalVisible = true">
                        Change Status
                        <font-awesome-icon widthAuto :icon="faArrowsRotate"></font-awesome-icon>
                    </vx-dropdown-item>

                    <vx-dropdown-item data-test="delete"
                        @click="deleteGroup()">
                        Delete
                        <font-awesome-icon widthAuto :icon="faTrash"></font-awesome-icon>
                    </vx-dropdown-item>
                </template>
            </vx-dropdown>

            <update-group-status-modal v-if="props.isKiosk"
                v-model:visible="statusModalVisible"
                :team-id="group.team_id"
                :group-id="group.group_id"
                :group-name="group.name"
                @updated="onStatusUpdated"
            />

            <vx-button :href="editHref"
                data-test="edit"
                :color="buttonColor"
                :outline="!setupRequired">
                {{ setupRequired ? 'Setup' : 'Edit' }} <font-awesome-icon widthAuto :icon="faFilePen" />
            </vx-button>
        </vx-table-row-buttons>

    </vx-table-row>
</template>

<script setup>
import {
    faArrowsRotate,
    faAsterisk,
    faBuildingFlag,
    faEllipsis,
    faFilePen,
    faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxBadge,
    VxButton,
    VxDropdown,
    VxDropdownItem,
    VxIconLoading,
    VxPopover,
    VxSwal,
    VxTableRow,
    VxTableRowButtons,
    VxTableRowItem,
    VxTableRowTitle,
} from '@voxie/frontend-components';
import { computed, ref } from "vue";
import { groupsClient, groupUsersClient } from "~/services";
import { throwSwalRequestError } from "~/utils/request";
import { formatNumber } from '~/utils/helpers';
import GroupStatusBadge from '~/components/general/GroupStatusBadge.vue';
import UpdateGroupStatusModal from '~/components/kiosk/kiosk-groups/list/UpdateGroupStatusModal.vue';

const group = defineModel({
    type: Object,
    required: true,
});

const props = defineProps({
    editGroupHref: {
        type: Function,
        default: null,
    },
    isKiosk: {
        type: Boolean,
        default: false,
    },
    isAdminOrOwner: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['deleted']);

const usersCount = ref('0');
const loadingUsersCount = ref(true)

const statusModalVisible = ref(false);

const onStatusUpdated = (status) => {
    group.value.status = status;
};

const setupRequired = computed(() => !group.value.name?.length)

const rowClasses = computed(() => {
    if (setupRequired.value) {
        return { 'border-rose-300! bg-rose-50!': true };
    }

    const status = group.value.status;
    return {
        'bg-amber-50!': status === 'draft',
        'bg-rose-50!': status === 'suspended',
        'bg-slate-100!': status === 'closed',
    };
})

const statusButtonColorMap = {
    draft: 'info',
    suspended: 'danger',
    closed: 'muted-dark',
};

const buttonColor = computed(() => {
    if (setupRequired.value) {
        return 'danger';
    }

    return statusButtonColorMap[group.value.status] || 'muted';
})

const editHref = computed(() => {
    if (props.editGroupHref) {
        return props.editGroupHref(group.value);
    }

    return setupRequired.value
        ? `/settings/teams/${group.value.team_id}/groups/new?setup_group_id=${group.value.group_id}`
        : `/settings/teams/${group.value.team_id}/groups/${group.value.group_id}`;
})

const getUsersCount = () => {
    if (setupRequired.value) {
        loadingUsersCount.value = false;
        return;
    }

    groupUsersClient
        .countGroupUsers(group.value.team_id, group.value.group_id)
        .then(({ data }) => {
            usersCount.value = formatNumber(data.count);
        })
        .finally(() => {
            loadingUsersCount.value = false;
        });
};

const deleteGroup = () => VxSwal.confirmation(
        'Delete Group',
        'Are you sure? There is no undo.',
        'danger',
        () =>
            groupsClient.deleteGroup({ teamId: group.value.team_id }, group.value.group_id)
                .then(() => emit('deleted', group.value.group_id))
                .catch(throwSwalRequestError)
    );


getUsersCount();

</script>
